import axios, { AxiosError } from "axios";

const api = axios.create({ baseURL: "/api" });

/** Paths whose handlers in OMS Go (`internal/handler/dto/proposal_dto.go`)
 *  honour `Accept-Version` negotiation. Match must run on the URL the
 *  caller passed to axios (relative path, post-baseURL). Backend strips
 *  `/oms` prefix at api-gw; frontend keeps it. We match either form. */
const ACCEPT_VERSION_PATTERN = /^\/?(oms\/)?admin\/orders\b/;

const shouldInjectAcceptVersion = (url: string | undefined): boolean => {
  if (!url) return false;
  return ACCEPT_VERSION_PATTERN.test(url);
};

api.interceptors.request.use(
  (config) => {
    // Tenta obter o token do localStorage ou sessionStorage
    const token =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (token) {
      config.headers.authorization = `Bearer ${token}`;
    }
    // ADR-021: forward the operator's tenant selection for the multi-tenant
    // backoffice. The api-gw validates it against the caller's server-derived
    // grant and returns 403 if not allowed — the client never authorises itself.
    const selectedTenant = localStorage.getItem("selectedTenantId");
    if (selectedTenant) {
      config.headers["X-Selected-Tenant-ID"] = selectedTenant;
    }
    // PR 6b — inject Accept-Version on OMS admin order routes so the
    // backend's dto.NegotiateVersion returns "v1" deterministically and
    // unknown versions surface as 406 (mapped to "version_mismatch" by
    // helpers/apiErrors.ts). See ADR-6b §B1.
    if (shouldInjectAcceptVersion(config.url)) {
      // Don't overwrite if a call already set its own Accept-Version
      // (defensive — no current call site does, but keep idempotent).
      if (!config.headers["Accept-Version"]) {
        config.headers["Accept-Version"] = "v1";
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Paths that validate the session itself — a 401/403 here means the session
// is truly dead and the user must re-authenticate.
const AUTH_PATHS = ["/settings", "/auth/", "/auth/profile"];

const isAuthPath = (url: string | undefined): boolean => {
  if (!url) return false;
  return AUTH_PATHS.some((p) => url.includes(p));
};

// Admin catalog endpoint. Exact match only: `/catalog/metadata/categories` is a
// feature endpoint whose 401/403 must not wipe the session. Only a 401 (dead
// token) triggers re-auth here; 403 is a permission problem, not expiry.
const isAdminCategoriesPath = (url: string | undefined): boolean => {
  if (!url) return false;
  return (
    url === "/categories" ||
    url.startsWith("/categories/") ||
    url.startsWith("/categories?")
  );
};

api.interceptors.response.use(
  (config) => {
    // Verifica se o token foi atualizado e salva no localStorage ou sessionStorage
    const authHeader =
      config.headers.authorization ||
      config.headers.Authorization ||
      (typeof config.headers.get === "function"
        ? config.headers.get("authorization")
        : undefined);
    const token =
      typeof authHeader === "string" ? authHeader.split(" ")[1] : undefined;
    if (token) {
      localStorage.setItem("authToken", token);
      sessionStorage.setItem("authToken", token);
    }
    return config;
  },
  (error: AxiosError) => {
    const status = error.response?.status || 0;
    const url = error.config?.url;

    if (status === 401 && isAdminCategoriesPath(url)) {
      // Admin catalog: 401 means the session is dead, 403 means "not allowed".
      // @ts-ignore
      api.clearToken();
    } else if ([401, 403, 423].includes(status) && isAuthPath(url)) {
      // Only force-logout when the auth/session endpoints reject the token.
      // Feature endpoints (OMS, payments, reviews…) may legitimately return
      // 401/403 without meaning the session is invalid — let callers handle.
      // @ts-ignore
      api.clearToken();
    }
    return Promise.reject(error);
  },
);

// @ts-ignore
api.clearToken = async () => {
  // Limpa o token do localStorage e sessionStorage
  localStorage.removeItem("authToken");
  sessionStorage.removeItem("authToken");
  // Redireciona para a página de login
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

export default api;
